import { memberApi } from '@/lib/api/members.api';
import { projectMemberApi } from '@/lib/api/project-members.api';
import { requestDedup } from './request-deduplication.service';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/services/project-members-cache.service');

interface Member {
  id: string;
  userId?: string | null;
  memberId?: string | null;
  user: {
    id: string;
    displayName: string | null;
    email: string;
    image?: string;
    firstName?: string | null;
    lastName?: string | null;
  };
}

const getSortableUserName = (user: Member['user']): string => {
  if (user.displayName?.trim()) {
    return user.displayName.trim();
  }

  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  if (fullName) {
    return fullName;
  }

  if (user.email) {
    const [localPart] = user.email.split('@');
    return localPart || user.email;
  }

  return 'Unknown User';
};

const compareMembersByUserName = (a: Member, b: Member): number => (
  getSortableUserName(a.user).localeCompare(getSortableUserName(b.user), undefined, { sensitivity: 'base' })
);

interface CacheEntry {
  members: Member[];
  timestamp: number;
  promise?: Promise<Member[]>;
}

class ProjectMembersCacheService {
  private cache: Map<string, CacheEntry> = new Map();
  private readonly CACHE_TTL = 10 * 60 * 1000; // 10 minutes

  /**
   * Get members for a project (or organization)
   */
  async getProjectMembers(projectId: string, organizationId: string): Promise<Member[]> {
    const cacheKey = `${organizationId}:${projectId || 'org'}`;
    const now = Date.now();
    const cached = this.cache.get(cacheKey);

    // Return cached data if still valid
    if (cached && (now - cached.timestamp < this.CACHE_TTL)) {
      return cached.promise || cached.members;
    }

    // If there's an ongoing request, return its promise
    if (cached?.promise) {
      return cached.promise;
    }

    // Create new request
    const promise = this.fetchMembers(organizationId, projectId)
      .then(members => {
        // Update cache with resolved data
        this.cache.set(cacheKey, {
          members,
          timestamp: now,
          promise: undefined
        });
        return members;
      })
      .catch(error => {
        // Remove failed request from cache
        this.cache.delete(cacheKey);
        throw error;
      });

    // Store promise in cache
    this.cache.set(cacheKey, {
      members: [],
      timestamp: now,
      promise
    });

    return promise;
  }

  /**
   * Fetch members from API - prioritize project members, fallback to organization
   */
  private async fetchMembers(organizationId: string, projectId?: string): Promise<Member[]> {
    try {
      // If projectId is provided, try to get project-specific members first
      if (projectId && projectId !== 'org') {
        try {
          const projectMembersResponse = await projectMemberApi.getProjectMembers(projectId, {
            limit: 100,
            page: 1
          });
          
          if (projectMembersResponse?.data?.length > 0) {
            return projectMembersResponse.data.map(member => ({
              id:
                member.userId ||
                member.user?.id ||
                member.memberId ||
                member.id,
              userId: member.userId,
              memberId: member.memberId || member.id,
              user: {
                id:
                  member.userId ||
                  member.user?.id ||
                  member.memberId ||
                  member.id,
                displayName: member.user?.displayName || member.user?.email?.split('@')[0] || 'Unknown',
                email: member.user?.email || '',
                image: member.user?.image,
                firstName: member.user?.firstName,
                lastName: member.user?.lastName
              }
            })).sort(compareMembersByUserName);
          }
        } catch (projectError) {
          logger.warn(`[ProjectMembersCache] Failed to fetch project members for ${projectId}, falling back to organization members:`, projectError);
        }
      }

      // Fallback to organization members
      const response = await memberApi.getMembersForDisplay({
        organizationId,
        limit: 100,
        page: 1
      });

      if (!response?.data) {
        return [];
      }

      return response.data.map(member => ({
        id: member.userId || member.user?.id || member.id,
        userId: member.userId || member.user?.id || member.id,
        memberId: member.id,
        user: {
          id: member.userId || member.user?.id || member.id,
          displayName: member.user?.displayName || member.user?.email?.split('@')[0] || 'Unknown',
          email: member.user?.email || '',
          image: member.user?.image,
          firstName: member.user?.firstName,
          lastName: member.user?.lastName
        }
      })).sort(compareMembersByUserName);
    } catch (error) {
      logger.error('[ProjectMembersCache] Failed to fetch members:', error);
      throw error;
    }
  }

  /**
   * Get members as assignee format with deduplication
   */
  async getAssignableUsers(projectId: string, organizationId: string): Promise<Array<{
    id: string;
    userId?: string | null;
    memberId?: string | null;
    name: string;
    email: string;
    avatarUrl?: string;
  }>> {
    // Use request deduplication to prevent multiple simultaneous requests
    return requestDedup.dedupe(
      `assignable-users-${organizationId}-${projectId}`,
      async () => {
        const members = await this.getProjectMembers(projectId, organizationId);
        
        return members.map(member => {
          // Enhanced name computation to match tasks.api.ts logic more closely
          const user = member.user;
          let name = user.displayName;
          
          if (!name || name === user.email?.split('@')[0]) {
            const firstName = user.firstName || '';
            const lastName = user.lastName || '';
            if (firstName || lastName) {
              name = `${firstName} ${lastName}`.trim();
            }
          }
          
          // Fallback to email prefix if name is still not resolved
          if (!name) {
            name = user.email?.split('@')[0] || 'Unknown User';
          }
          
          return {
            id: member.userId || member.user.id || member.id,
            userId: member.userId || member.user.id,
            memberId: member.memberId || null,
            name: name, // Use the computed name
            email: user.email,
            avatarUrl: user.image
          };
        });
      },
      2000 // Cache for 2 seconds to handle rapid component mounts
    );
  }

  /**
   * Invalidate cache for a specific project/organization
   */
  invalidate(organizationId: string, projectId?: string): void {
    const cacheKey = `${organizationId}:${projectId || 'org'}`;
    this.cache.delete(cacheKey);
  }

  /**
   * Clear entire cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Preload members for multiple projects
   */
  async preloadProjects(organizationId: string, projectIds: string[]): Promise<void> {
    const promises = projectIds.map(projectId => 
      this.getProjectMembers(projectId, organizationId).catch(error => {
        logger.error(`[ProjectMembersCache] Failed to preload project ${projectId}:`, error);
      })
    );
    
    await Promise.all(promises);
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    entries: Array<{ key: string; hasData: boolean; age: number }>;
  } {
    const now = Date.now();
    const entries = Array.from(this.cache.entries()).map(([key, entry]) => ({
      key,
      hasData: entry.members.length > 0,
      age: now - entry.timestamp
    }));

    return {
      size: this.cache.size,
      entries
    };
  }
}

// Export singleton instance
export const projectMembersCache = new ProjectMembersCacheService();
